import {
    AnalyticsUserEvent,
    type DepositAccount,
    type ImAccount,
    isRetirementRegistrationType,
} from '@fruitful/types';
import { router } from 'expo-router';
import { useAtomValue, useSetAtom } from 'jotai';
import * as React from 'react';
import { useMemo, useState } from 'react';
import {
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    FootnoteRegular,
    FullScreenSpinner,
    ScreenContainer,
    YStack,
} from 'smoothie';

import { INVESTMENT_NOT_FDIC_INSURED } from '../../../banking/components/Disclosures';
import SelectAccountSheet from '../../../banking/components/transfers/SelectAccountSheet';
import TransferAccountLineItem from '../../../banking/components/transfers/TransferAccountLineItem';
import useGetInternalDepositAccounts from '../../../banking/hooks/useGetInternalDepositAccounts';
import { useTrackViewOnFocus } from '../../../events/hooks/useTrackViewOnFocus';
import { track } from '../../../logger/Logger';
import { useGetApexAccounts } from '../../hooks/useGetApexAccounts';
import {
    type ApexWithdrawalRequest,
    apexWithdrawalRequestAtom,
    selectedApexTransferAccount,
} from '../../state/investmentAtoms';
import { ApexAccountLineItem } from './ApexAccountLineItem';
import SelectApexAccountSheet from './SelectApexAccountSheet';

export const ApexWithdrawalForm = () => {
    useTrackViewOnFocus('Investment/Transfer/Withdrawal');

    const preselectedApexTransferAccount = useAtomValue(
        selectedApexTransferAccount,
    );
    const setApexWithdrawalRequest = useSetAtom(apexWithdrawalRequestAtom);

    const [isSelectFromAccountSheetOpen, setIsSelectFromAccountSheetOpen] =
        useState(false);
    const [isSelectApexAccountSheetOpen, setIsSelectApexAccountSheetOpen] =
        useState(false);

    const [selectedDepositAccount, setSelectedDepositAccount] =
        useState<DepositAccount | null>(null);

    const [selectedApexAccount, setSelectedApexAccount] =
        useState<ImAccount | null>(preselectedApexTransferAccount);

    const { data: depositAccounts, isLoading: isLoadingDepositAccounts } =
        useGetInternalDepositAccounts({});

    const { data: apexAccounts, isLoading: isLoadingApexAccounts } =
        useGetApexAccounts();

    const isValid = useMemo(() => {
        if (selectedApexAccount == null) {
            return false;
        }
        if (selectedDepositAccount == null) {
            return false;
        }
        return selectedApexAccount.realtimeBalanceData.accountEquityCents > 0;
    }, [selectedApexAccount, selectedDepositAccount]);

    const isRetirementAccount = useMemo(
        () =>
            isRetirementRegistrationType(selectedApexAccount?.registrationType),
        [selectedApexAccount?.registrationType],
    );

    if (isLoadingDepositAccounts || isLoadingApexAccounts) {
        return <FullScreenSpinner />;
    }

    const withdrawalRequest: Partial<ApexWithdrawalRequest> | null =
        selectedApexAccount != null && selectedDepositAccount != null
            ? {
                  isRetirementAccount,
                  toAccount: selectedDepositAccount,
                  fromAccount: selectedApexAccount,
              }
            : null;

    return (
        <ScreenContainer hasBackButton={true} title="Withdraw funds">
            <YStack gap={24}>
                <BodySmallRegular color="$gray1000">From</BodySmallRegular>
                <ApexAccountLineItem
                    placeholder="Select an investment account"
                    onPress={() => {
                        setIsSelectApexAccountSheetOpen(true);
                    }}
                    apexAccount={selectedApexAccount}
                />
                <BodySmallRegular color="$gray1000">To</BodySmallRegular>
                <TransferAccountLineItem
                    placeholder="Select a bank account"
                    onPress={() => {
                        setIsSelectFromAccountSheetOpen(true);
                    }}
                    depositAccount={selectedDepositAccount}
                />
                <Button
                    disabled={!isValid}
                    text="Continue"
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.LARGE}
                    onPress={() => {
                        setApexWithdrawalRequest(withdrawalRequest);
                        track(AnalyticsUserEvent.IM_WITHDRAWAL_INITIATED);
                        router.navigate(
                            '/investments/transfer/withdrawal-amount',
                        );
                    }}
                />
                <FootnoteRegular color="$gray900">
                    {INVESTMENT_NOT_FDIC_INSURED}
                </FootnoteRegular>
                <FootnoteRegular color="$gray900">
                    Fruitful is a financial technology company, not a bank.
                    Deposit accounts provided by Emigrant Bank, Member FDIC.
                </FootnoteRegular>
            </YStack>
            {depositAccounts && (
                <>
                    <SelectAccountSheet
                        isOpen={isSelectFromAccountSheetOpen}
                        depositAccounts={depositAccounts}
                        onClose={() => {
                            setIsSelectFromAccountSheetOpen(false);
                        }}
                        onPlaidExit={() => {}}
                        onSelectAccount={(account) => {
                            setSelectedDepositAccount(account);
                        }}
                        enableLinkingNewAccount={false}
                        onNeedsReview={() => {}}
                    />
                </>
            )}
            {apexAccounts && (
                <>
                    <SelectApexAccountSheet
                        isOpen={isSelectApexAccountSheetOpen}
                        apexAccounts={apexAccounts}
                        onClose={() => {
                            setIsSelectApexAccountSheetOpen(false);
                        }}
                        onSelectAccount={(account) => {
                            setSelectedApexAccount(account);
                        }}
                    />
                </>
            )}
        </ScreenContainer>
    );
};
